import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import { Activity, Landmark, DollarSign, Clock, CheckCircle2, AlertCircle, Sparkles, Filter, RefreshCw, Loader2, TrendingUp, Inbox, ShieldAlert } from 'lucide-react';
import { inboxStatsAPI } from '../api';
import { ErrorBoundary } from './ErrorBoundary';

interface DashboardProps {
  demands: any[]; // mantido para compatibilidade, mas não é mais usado para calcular métricas
  onOpenNewDemand: () => void;
}

interface DashboardStats {
  recebidas: number;
  processando: number;
  revisaoObrigatoria: number;
  resolvidasHoje: number;
  recebidasHoje: number;
  total: number;
  emAndamento: number;
  arquivadas: number;
  indeferidas: number;
  aprovadas: number;
  encaminhadas: number;
  totalValueRequested: number;
  totalValueApproved: number;
  avgResolutionDays: number;
  byArea: { name: string; value: number }[];
  byCabinet: { name: string; value: number }[];
  byStatus: { name: string; value: number }[];
  last7Days: { date: string; quantidade: number }[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

const CABINET_LABELS: Record<string, string> = {
  STATE: 'Estadual (Dani Alonso)',
  FEDERAL: 'Federal (Cap. Augusto)',
  BOTH: 'Ambos',
};
const CABINET_COLORS: Record<string, string> = {
  STATE: '#2563EB',
  FEDERAL: '#DC2626',
  BOTH: '#F59E0B',
};
const STATUS_COLORS: Record<string, string> = {
  RECEBIDA: '#3B82F6',
  EM_TRIAGEM: '#F59E0B',
  EM_ANALISE: '#F97316',
  APROVADA: '#10B981',
  CONCLUIDA: '#059669',
  ENCAMINHADA: '#6366F1',
  ARQUIVADA: '#6B7280',
  INDEFERIDA: '#EF4444',
};
const STATUS_LABELS: Record<string, string> = {
  RECEBIDA: 'Recebida',
  EM_TRIAGEM: 'Em Triagem',
  EM_ANALISE: 'Em Análise',
  APROVADA: 'Aprovada',
  CONCLUIDA: 'Concluída',
  ARQUIVADA: 'Arquivada',
  INDEFERIDA: 'Indeferida',
};

// ─── Mobile-only sub-components ──────────────────────────────────────────────

type MetricColor = 'blue' | 'amber' | 'emerald' | 'purple' | 'red';

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: MetricColor;
}

const COLOR_MAP: Record<MetricColor, { bg: string; icon: string; value: string }> = {
  blue:    { bg: 'bg-blue-50 dark:bg-blue-950/30',       icon: 'text-blue-500',    value: 'text-blue-700 dark:text-blue-300' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-950/30',     icon: 'text-amber-500',   value: 'text-amber-700 dark:text-amber-300' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', icon: 'text-emerald-500', value: 'text-emerald-700 dark:text-emerald-300' },
  purple:  { bg: 'bg-purple-50 dark:bg-purple-950/30',   icon: 'text-purple-500',  value: 'text-purple-700 dark:text-purple-300' },
  red:     { bg: 'bg-red-50 dark:bg-red-950/30',         icon: 'text-red-500',     value: 'text-red-700 dark:text-red-300' },
};

const MetricCard: React.FC<MetricCardProps> = ({ icon, label, value, color }) => {
  const c = COLOR_MAP[color];
  return (
    <div className={`${c.bg} rounded-2xl p-4 flex flex-col gap-2 border border-slate-200 dark:border-slate-800 min-h-[80px] justify-between`}>
      <div className={`${c.icon} w-8 h-8 flex items-center justify-center`}>{icon}</div>
      <div>
        <p className={`text-2xl font-extrabold leading-none ${c.value}`}>{value}</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">{label}</p>
      </div>
    </div>
  );
};

interface ActionCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  description: string;
  accentClass: string;
  borderClass: string;
  countClass: string;
}

const ActionCard: React.FC<ActionCardProps> = ({ icon, label, count, description, accentClass, borderClass, countClass }) => (
  <div className={`flex items-center gap-4 p-4 rounded-2xl border ${borderClass} bg-white dark:bg-slate-900`}>
    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${accentClass}`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-slate-800 dark:text-slate-100 text-sm">{label}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{description}</p>
    </div>
    <span className={`text-2xl font-extrabold flex-shrink-0 ${countClass}`}>
      {count}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

export const Dashboard: React.FC<DashboardProps> = ({ demands, onOpenNewDemand }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterCabinet, setFilterCabinet] = useState('');
  const [filterArea, setFilterArea] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const data = await inboxStatsAPI.stats() as any;
      setStats(data);
    } catch (e) {
      console.error('Erro ao buscar estatísticas do dashboard', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Atualiza a cada 30s
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="text-sm font-medium">Carregando métricas reais do banco...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <p>Não foi possível carregar o dashboard. Verifique a API.</p>
      </div>
    );
  }

  // Filtrar dados dos gráficos pelo filtro selecionado
  const areaChartData = stats.byArea
    .filter(a => !filterArea || a.name === filterArea)
    .map(a => ({ name: a.name, Quantidade: a.value }));

  const cabinetPieData = stats.byCabinet
    .filter(c => !filterCabinet || c.name === filterCabinet)
    .map(c => ({
      name: CABINET_LABELS[c.name] || c.name,
      value: c.value,
      color: CABINET_COLORS[c.name] || '#9CA3AF',
    }))
    .filter(c => c.value > 0);

  const statusPieData = stats.byStatus.map(s => ({
    name: STATUS_LABELS[s.name] || s.name,
    value: s.value,
    color: STATUS_COLORS[s.name] || '#9CA3AF',
  })).filter(s => s.value > 0);

  // ── Action-needed counts (computed from demands prop) ──────────────────────
  const now = Date.now();
  const fiveDaysMs = 5 * 24 * 60 * 60 * 1000;

  const urgentesCount = demands.filter((d: any) => d.priority === 'URGENTE').length;
  const aguardandoCount = demands.filter((d: any) => d.status === 'RECEBIDA').length;
  const vencidasCount = demands.filter((d: any) =>
    (d.status === 'RECEBIDA' || d.status === 'EM_ANALISE') &&
    now - new Date(d.createdAt).getTime() > fiveDaysMs
  ).length;

  return (
    <div className="space-y-6 text-left">

      {/* 1. HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-sans">Dashboard Executivo</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Métricas em tempo real
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchStats}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500 font-semibold text-xs rounded-xl hover:bg-slate-200 dark:hover:bg-slate-800 transition-all"
            title="Atualizar métricas"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </button>
          <button
            onClick={onOpenNewDemand}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-blue-500/10 transition-all"
          >
            <Sparkles className="w-4 h-4" /> Nova Demanda
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          MOBILE-ONLY SECTION  (hidden on md and above)
          ══════════════════════════════════════════════════════ */}

      {/* Mobile: 2-col metric card grid */}
      <div className="md:hidden grid grid-cols-2 gap-3">
        <MetricCard
          icon={<Activity className="w-5 h-5" />}
          label="Recebidas Hoje"
          value={stats.recebidasHoje}
          color="blue"
        />
        <MetricCard
          icon={<Inbox className="w-5 h-5" />}
          label="Fila de Triagem"
          value={stats.recebidas}
          color="amber"
        />
        <MetricCard
          icon={<CheckCircle2 className="w-5 h-5" />}
          label="Concluídas Hoje"
          value={stats.resolvidasHoje}
          color="emerald"
        />
        <MetricCard
          icon={<Activity className="w-5 h-5" />}
          label="Total no Sistema"
          value={stats.total}
          color="blue"
        />
        <MetricCard
          icon={<AlertCircle className="w-5 h-5" />}
          label="Em Análise"
          value={stats.processando}
          color="amber"
        />
        <MetricCard
          icon={<Clock className="w-5 h-5" />}
          label="Resolução Média"
          value={stats.avgResolutionDays > 0 ? `${stats.avgResolutionDays}d` : '—'}
          color="purple"
        />
        <MetricCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Em Triagem"
          value={stats.emAndamento}
          color="purple"
        />
        <MetricCard
          icon={<ShieldAlert className="w-5 h-5" />}
          label="Revisão Obrigatória"
          value={stats.revisaoObrigatoria}
          color="red"
        />
      </div>

      {/* Mobile: Atenção Necessária */}
      <div className="md:hidden space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-0.5">
          ⚠️ Atenção Necessária
        </h2>
        <ActionCard
          icon={<ShieldAlert className="w-5 h-5 text-red-500" />}
          label="Urgentes"
          count={urgentesCount}
          description="Demandas com prioridade URGENTE aguardando ação"
          accentClass="bg-red-100 dark:bg-red-950/40"
          borderClass="border-red-200 dark:border-red-900/60"
          countClass="text-red-600 dark:text-red-400"
        />
        <ActionCard
          icon={<Inbox className="w-5 h-5 text-amber-500" />}
          label="Aguardando Análise"
          count={aguardandoCount}
          description="Demandas recebidas ainda não triadas"
          accentClass="bg-amber-100 dark:bg-amber-950/40"
          borderClass="border-amber-200 dark:border-amber-900/60"
          countClass="text-amber-600 dark:text-amber-400"
        />
        <ActionCard
          icon={<Clock className="w-5 h-5 text-blue-500" />}
          label="Vencidas"
          count={vencidasCount}
          description="Abertas há mais de 5 dias sem avanço (RECEBIDA ou EM_ANALISE)"
          accentClass="bg-blue-100 dark:bg-blue-950/40"
          borderClass="border-blue-200 dark:border-blue-900/60"
          countClass="text-blue-600 dark:text-blue-400"
        />
      </div>

      {/* ══════════════════════════════════════════════════════
          DESKTOP-ONLY SECTIONS (hidden below md)
          ══════════════════════════════════════════════════════ */}

      {/* 2. FILTROS — desktop only */}
      <div className="hidden md:flex glass-panel p-4 rounded-2xl border border-slate-200 dark:border-slate-800/80 flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-1.5 text-slate-450 shrink-0 font-bold uppercase tracking-wider text-[10px]">
          <Filter className="w-3.5 h-3.5 text-blue-500" /> Filtros nos Gráficos:
        </div>
        <select value={filterCabinet} onChange={e => setFilterCabinet(e.target.value)}
          className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none font-semibold">
          <option value="">Gabinete (Todos)</option>
          <option value="STATE">Dani Alonso (Estadual)</option>
          <option value="FEDERAL">Capitão Augusto (Federal)</option>
          <option value="BOTH">Ambos</option>
        </select>
        <select value={filterArea} onChange={e => setFilterArea(e.target.value)}
          className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none font-semibold">
          <option value="">Área Temática (Todas)</option>
          {stats.byArea.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
        </select>
        {(filterCabinet || filterArea) && (
          <button onClick={() => { setFilterCabinet(''); setFilterArea(''); }}
            className="flex items-center gap-1 px-3 py-2 bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 font-bold transition-all">
            <RefreshCw className="w-3 h-3" /> Limpar
          </button>
        )}
      </div>

      {/* 3. CARDS PRINCIPAIS — 3 grandes (desktop only) */}
      <div className="hidden md:grid md:grid-cols-3 gap-4">
        {/* Entrada Diária */}
        <div className="bg-gradient-to-tr from-blue-600/10 to-indigo-600/10 border border-blue-500/20 dark:border-blue-550/30 p-5 rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider font-mono">Entrada Diária</span>
            <h3 className="text-3xl font-extrabold mt-1 text-slate-800 dark:text-slate-100">{stats.recebidasHoje}</h3>
            <p className="text-slate-400 text-[10px] mt-1">Demandas Recebidas Hoje</p>
          </div>
          <div className="p-3 bg-blue-500 text-white rounded-2xl shadow-lg shadow-blue-500/25">
            <Activity className="w-6 h-6" />
          </div>
        </div>

        {/* Fila de Triagem */}
        <div className="bg-gradient-to-tr from-amber-600/10 to-amber-500/10 border border-amber-500/20 dark:border-amber-550/30 p-5 rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider font-mono">Fila de Triagem</span>
            <h3 className="text-3xl font-extrabold mt-1 text-slate-800 dark:text-slate-100">{stats.recebidas}</h3>
            <p className="text-slate-400 text-[10px] mt-1">Demandas Pendentes (Aguardando Triagem)</p>
          </div>
          <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-lg shadow-amber-550/25">
            <Inbox className="w-6 h-6" />
          </div>
        </div>

        {/* Concluídas Hoje */}
        <div className="bg-gradient-to-tr from-emerald-600/10 to-emerald-500/10 border border-emerald-500/20 dark:border-emerald-550/30 p-5 rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider font-mono">Concluídas Hoje</span>
            <h3 className="text-3xl font-extrabold mt-1 text-slate-800 dark:text-slate-100">{stats.resolvidasHoje}</h3>
            <p className="text-slate-400 text-[10px] mt-1">Demandas Concluídas Hoje</p>
          </div>
          <div className="p-3 bg-emerald-500 text-white rounded-2xl shadow-lg shadow-emerald-550/25">
            <CheckCircle2 className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* 4. KPI SECUNDÁRIOS — 4 menores (desktop only) */}
      <div className="hidden md:grid md:grid-cols-4 gap-4">
        {/* Filtro Ativo / Total */}
        <div className="glass-panel p-4 rounded-2xl flex items-center gap-3 border border-slate-200 dark:border-slate-800">
          <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-600 dark:text-blue-400">
            <Activity className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Total no Sistema</p>
            <h4 className="text-lg font-bold mt-0.5">{stats.total}</h4>
          </div>
        </div>

        {/* Verba Solicitada */}
        <div className="glass-panel p-4 rounded-2xl flex items-center gap-3 border border-slate-200 dark:border-slate-800">
          <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400">
            <DollarSign className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Verba Solicitada</p>
            <h4 className="text-xs font-bold mt-0.5 text-slate-700 dark:text-white truncate">
              {stats.totalValueRequested > 0 ? formatCurrency(stats.totalValueRequested) : 'R$ 0,00'}
            </h4>
          </div>
        </div>

        {/* Emendas Aprovadas */}
        <div className="glass-panel p-4 rounded-2xl flex items-center gap-3 border border-slate-200 dark:border-slate-800">
          <div className="p-2.5 bg-emerald-500/10 rounded-xl text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Emendas Aprovadas</p>
            <h4 className="text-xs font-bold mt-0.5 text-emerald-600 dark:text-emerald-400 truncate">
              {stats.totalValueApproved > 0 ? formatCurrency(stats.totalValueApproved) : 'R$ 0,00'}
            </h4>
          </div>
        </div>

        {/* Resolução Média */}
        <div className="glass-panel p-4 rounded-2xl flex items-center gap-3 border border-slate-200 dark:border-slate-800">
          <div className="p-2.5 bg-purple-500/10 rounded-xl text-purple-600 dark:text-purple-400">
            <Clock className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Resolução Média</p>
            <h4 className="text-lg font-bold mt-0.5">
              {stats.avgResolutionDays > 0 ? `${stats.avgResolutionDays} dias` : '—'}
            </h4>
          </div>
        </div>
      </div>

      {/* 5. STATUS CARDS — linha visual (desktop only) */}
      <div className="hidden md:grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: 'Aguardando Triagem', value: stats.recebidas, color: 'text-blue-500', bg: 'bg-blue-500/10', icon: <Inbox className="w-4 h-4" /> },
          { label: 'Em Análise', value: stats.processando, color: 'text-amber-500', bg: 'bg-amber-500/10', icon: <AlertCircle className="w-4 h-4" /> },
          { label: 'Em Triagem', value: stats.emAndamento, color: 'text-purple-500', bg: 'bg-purple-500/10', icon: <Activity className="w-4 h-4" /> },
          { label: 'Revisão Obrigatória', value: stats.revisaoObrigatoria, color: 'text-red-500', bg: 'bg-red-500/10', icon: <ShieldAlert className="w-4 h-4" /> },
          { label: 'Aprovadas', value: stats.aprovadas, color: 'text-emerald-600', bg: 'bg-emerald-500/10', icon: <CheckCircle2 className="w-4 h-4" /> },
          { label: 'Concluídas Hoje', value: stats.resolvidasHoje, color: 'text-emerald-500', bg: 'bg-emerald-400/10', icon: <TrendingUp className="w-4 h-4" /> },
        ].map(item => (
          <div key={item.label} className="glass-panel p-4 rounded-2xl border border-slate-200 dark:border-slate-800 text-center">
            <div className={`${item.bg} ${item.color} w-8 h-8 rounded-xl flex items-center justify-center mx-auto mb-2`}>
              {item.icon}
            </div>
            <p className={`text-2xl font-extrabold ${item.color}`}>{item.value}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{item.label}</p>
          </div>
        ))}
      </div>

      {/* 6. GRÁFICOS — desktop only */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Recebidas nos Últimos 7 Dias */}
        <div className="glass-panel p-5 rounded-3xl lg:col-span-7 flex flex-col h-[320px] border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <h2 className="font-bold text-sm font-sans">Demandas — Últimos 7 Dias</h2>
          </div>
          <div className="flex-1 w-full text-[10px]">
            {stats.last7Days.some(d => d.quantidade > 0) ? (
              <ErrorBoundary silent>
                <ResponsiveContainer width="100%" height="100%" minWidth={99} minHeight={99}>
                  <LineChart data={stats.last7Days} margin={{ top: 10, right: 10, left: -25, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" className="dark:opacity-10" />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="quantidade" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 4 }} activeDot={{ r: 6 }} name="Demandas" />
                  </LineChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            ) : (
              <div className="flex items-center justify-center h-full italic text-slate-400">Nenhuma demanda nos últimos 7 dias.</div>
            )}
          </div>
        </div>

        {/* Status Operacional */}
        <div className="glass-panel p-5 rounded-3xl lg:col-span-5 flex flex-col h-[320px] border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <h2 className="font-bold text-sm font-sans">Status Operacional</h2>
          </div>
          <div className="flex-1 flex items-center justify-center text-[10px]">
            {statusPieData.length > 0 ? (
              <ErrorBoundary silent>
                <ResponsiveContainer width="100%" height="100%" minWidth={99} minHeight={99}>
                  <PieChart>
                    <Pie data={statusPieData} cx="50%" cy="45%" outerRadius={70}
                      label={({ name, percent }) => `${name} (${percent ? (percent * 100).toFixed(0) : 0}%)`}
                      dataKey="value">
                      {statusPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            ) : (
              <div className="italic text-slate-450">Nenhum dado disponível.</div>
            )}
          </div>
        </div>
      </div>

      {/* 7. ÁREA TEMÁTICA + GABINETE — desktop only */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Barras por Área */}
        <div className="glass-panel p-5 rounded-3xl lg:col-span-8 flex flex-col h-[300px] border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="w-5 h-5 text-blue-500" />
            <h2 className="font-bold text-sm font-sans">Demandas por Área Temática</h2>
          </div>
          <div className="flex-1 w-full text-[10px]">
            {areaChartData.length > 0 ? (
              <ErrorBoundary silent>
                <ResponsiveContainer width="100%" height="100%" minWidth={99} minHeight={99}>
                  <BarChart data={areaChartData} margin={{ top: 10, right: 10, left: -25, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" className="dark:opacity-10" />
                    <XAxis dataKey="name" tickLine={false} axisLine={false} />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(226,232,240,0.4)' }} />
                    <Bar dataKey="Quantidade" radius={[4, 4, 0, 0]} barSize={22}>
                      {areaChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={filterArea === entry.name ? '#2563EB' : '#60A5FA'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            ) : (
              <div className="flex items-center justify-center h-full italic text-slate-400">Nenhum dado sob os filtros selecionados.</div>
            )}
          </div>
        </div>

        {/* Pizza por Gabinete */}
        <div className="glass-panel p-5 rounded-3xl lg:col-span-4 flex flex-col h-[300px] border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-red-500" />
            <h2 className="font-bold text-sm font-sans">Gabinete Destinatário</h2>
          </div>
          <div className="flex-1 flex items-center justify-center text-[10px]">
            {cabinetPieData.length > 0 ? (
              <ErrorBoundary silent>
                <ResponsiveContainer width="100%" height="100%" minWidth={99} minHeight={99}>
                  <PieChart>
                    <Pie data={cabinetPieData} cx="50%" cy="45%" innerRadius={45} outerRadius={70} paddingAngle={5} dataKey="value">
                      {cabinetPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </ErrorBoundary>
            ) : (
              <div className="italic text-slate-450">Nenhum dado disponível.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
